<template>
    <div class="edit-opportunity-modal-container">
        <div class="date-payout-image-container">
            <div class="date-payout-private-container">
                <div class="date-payout-fields">
                    <div class="date-input-wrapper">
                        <p class="title">Date *</p>
                        <label for="opportunity-date">Event date</label>
                        <input
                            ref="datepicker"
                            data-provide="datepicker"
                            id="date"
                            v-model="eventDate"
                            data-popup-id="date-popup"
                            autocomplete="off"
                            class="edit-form-control search-mobile-input datepicker-home main-text js-date-value js-key-submit"
                            type="text"
                            name="date"
                            placeholder="Enter event date"
                        />
                    </div>
                    <div class="payout-input-wrapper">
                        <p class="title">Artist Payout *</p>
                        <label for="opportunity-payout">Desired Payout</label>
                        <span class="payout-value-input">
                            <span v-if="artistPayout !== null && artistPayout !== ''">$</span>
                            <input
                                v-model="artistPayout"
                                class="payout-value"
                                name="artist-payout"
                                type="number"
                                placeholder="$600"
                            />
                        </span>
                    </div>
                </div>
                <div class="event-type-input-wrapper">
                    <div class="event-type-switch-label">
                        <span class="switch-label"> Private </span>
                        <SwitchToggle
                            :initialState="privateOpportunity"
                            @switch-checked="setPrivateOpportunity"
                            @switch-unchecked="setPublicOpportunity"
                        />
                    </div>
                    <p
                        v-if="!privateOpportunity"
                        class="invite-artist-description gray-text mb-0"
                    >
                        Open to all Hedlyner Artists
                    </p>
                    <p v-else class="invite-artist-description gray-text mb-0">
                        Only artists with a link can see the event
                    </p>
                </div>
            </div>
            <div class="event-image">
                <p>Cover Image</p>
                <div class="image-container" @click.once="uploadImage">
                    <img
                        :src="getEventImage ? getEventImage : getIcon + 'Upload-Circle.png'"
                        @click.self="uploadImage"
                        class="default-image"
                        :class="{
                            'upload-image': !!getEventImage
                        }"
                    />
                    <input
                        @change="selectFile($event)"
                        hidden
                        ref="photoUpload"
                        type="file"
                        class="photo-dwnld"
                    />
                </div>
            </div>
        </div>
        <div class="opportunity-description">
            <p class="title">Opportunity Description *</p>
            <div class="opportunity-detail-field">
                <p
                    :class="
                        opportunityDetail.length > 500
                            ? 'opportunity-detail-counter error-text'
                            : 'opportunity-detail-counter'
                    "
                >
                    {{ opportunityDetailCount }}
                </p>
                <textarea
                    class="opportunity-detail-input"
                    v-model="opportunityDetail"
                    type="text"
                    placeholder="Add details of this opportunity like desired duration, start time, any restrictions or other info that is useful for talent to apply."
                />
                <p v-if="opportunityDetail.length > 500" class="mb-0 error-text">
                    You have exceeded the maximum number of characters in this text field.
                </p>
            </div>
        </div>
        <div class="vibe-container">
            <p class="title">Vibe</p>
            <input
                v-model="keywordInput"
                :class="vibeErrors.error ? 'input-err' : ''"
                class="vibe-input"
                type="text"
                name="event-vibe"
                placeholder="Add more tags here"
                @input="validateKeywordInput(vibeErrors)"
                @keyup.enter="handleAddTag(vibeErrors.error)"
            />
            <div v-if="keywords.length" class="keywords">
                <div v-for="(tag, indx) in keywords" :key="indx" class="tag-wrapper">
                    <span @click="handleDeleteTag(indx)" class="tag">{{ tag }}</span>
                </div>
            </div>
        </div>
        <div class="btn-container">
            <button
                :class="{ 'disabled-btn': disabledSaveBtn }"
                :disabled="disabledSaveBtn"
                @click="updateEventData"
            >
                Save
            </button>
        </div>
    </div>
</template>
<script>
    import axios from 'axios'
    import moment from 'moment'
    import { notification } from '@/helpers/notification'
    import useKeywords from '@/composables/useKeywords.js'
    import SwitchToggle from '@/components/ui/toggle/Switch.vue'
    import { EventMixin } from '../../../mixins/event-list-calendar'

    export default {
        name: 'EditOpportunityModal',
        mixins: [EventMixin],
        components: {
            SwitchToggle
        },
        props: {
            event: {
                required: true,
                type: Object
            }
        },
        emits: ['action-completed'],
        setup() {
            const {
                keywordInput,
                suggestedKeywords,
                renderSuggestedKeywords,
                keywords,
                validateKeywordInput,
                handleAddTag,
                handleSelectTag,
                handleDeleteTag,
                populateKeywords,
                clearKeywords
            } = useKeywords([])

            return {
                keywordInput,
                suggestedKeywords,
                renderSuggestedKeywords,
                keywords,
                validateKeywordInput,
                handleAddTag,
                handleSelectTag,
                handleDeleteTag,
                populateKeywords,
                clearKeywords
            }
        },
        data: () => ({
            image: null,
            datepickerOptions: {
                format: 'D, MM dd',
                language: 'en'
            },
            opportunityDetail: '',
            eventDate: '',
            artistPayout: null,
            privateOpportunity: null,
            vibeErrors: {
                error: false
            }
        }),
        computed: {
            opportunityDetailCount: (self) => `${self.opportunityDetail.length} / 500`,
            disabledSaveBtn: (self) =>
                !self.eventDate ||
                !self.artistPayout ||
                !self.opportunityDetail ||
                self.opportunityDetail.length > 500,
            getEventImage() {
                return this.image ? this.image : this.event.image
            },
            getIcon() {
                return '/images/'
            }
        },
        created() {
            this.populateFields()
        },
        mounted() {
            this.$nextTick(this.initializeDatePicker)
        },
        methods: {
            async updateEventData() {
                try {
                    const payload = {}

                    if (this.eventDate) payload.start = this.eventDate
                    if (this.keywords.length) {
                        payload.keywords = this.keywords.join(', ')
                    } else {
                        payload.keywords = 'All Genres'
                    }
                    if (this.artistPayout) payload.budget = this.artistPayout
                    if (this.privateOpportunity !== null)
                        payload.private_opportunity = this.privateOpportunity
                    if (this.opportunityDetail)
                        payload.opportunity_detail = this.opportunityDetail
                    const response = await axios.put(
                        '/api/event/manage/update/' + this.event.id,
                        payload
                    )
                    notification('success', response.data.message)

                    this.$emit('action-completed', {
                        ...payload,
                        id: this.event.id,
                        start: moment(payload.start).format('YYYY-MM-DD'),
                        start_date: moment(payload.start).format('ddd, MMMM Do')
                    })
                    this.$bvModal.hide('modal-edit-event')
                } catch (error) {
                    notification('error', error)
                }
            },
            populateFields() {
                console.log(this.event)
                if (this.event.start) {
                    this.eventDate = this.formatEventDate(this.event.start)
                }
                if (this.event.opportunity_detail)
                    this.opportunityDetail = this.event.opportunity_detail
                if (this.event.vibe) this.keywords = this.event.vibe.split(',')
                this.artistPayout = this.event.budget_without_commission
                this.privateOpportunity = this.event.private_opportunity
            },
            formatEventDate(start) {
                if (!start) return undefined
                return start.includes('T')
                    ? moment(start).format('YYYY-MM-DD')
                    : start.split(' ')[0]
            },
            initializeDatePicker() {
                const datePickerInput = $(this.$refs.datepicker)

                const defaultDate = this.event.start
                    ? moment.utc(this.event.start).startOf('day').toDate()
                    : null

                datePickerInput.datepicker({
                    ...this.datepickerOptions,
                    startDate: new Date(),
                    defaultViewDate: defaultDate
                        ? moment(defaultDate).format('MM/DD/YYYY')
                        : null,
                    autoclose: true
                })

                if (defaultDate) {
                    datePickerInput.datepicker('setDate', defaultDate)
                    this.eventDate = moment(defaultDate).format('MM/DD/YYYY')
                }

                datePickerInput.on('changeDate', this.updateEventDate)
            },
            updateEventDate(e) {
                if (e.date) {
                    this.eventDate = moment(e.date).format('MM/DD/YYYY')
                }
            },
            uploadImage() {
                this.$refs.photoUpload.click()
            },
            selectFile(event) {
                const file = event.target.files[0]

                const reader = new FileReader()

                reader.onloadend = () => {
                    this.image = reader.result
                    this.uploadImageRequest()
                }

                reader.readAsDataURL(file)
            },
            uploadImageRequest() {
                if (this.event.image !== this.image) {
                    axios
                        .put('/api/event/manage/update/' + this.event.id, {
                            image: this.image
                        })
                        .then((response) => {
                            notification('success', response.data.message)
                            this.$emit('action-completed', {
                                image: this.image,
                                id: this.event.id
                            })
                        })
                        .catch((error) => {
                            notification('error', error)
                        })
                }
            },
            setPrivateOpportunity() {
                console.log(this.event)
                this.privateOpportunity = true
            },
            setPublicOpportunity() {
                console.log(this.event)
                this.privateOpportunity = false
            }
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    @media (max-width: 600px) {
        .title {
            font-size: 20px !important;
        }
        .edit-opportunity-modal-container {
            .opportunity-description {
                width: 100%;
            }
            .date-payout-fields {
                width: 100%;
                flex-direction: column !important;
                align-items: flex-start !important;
                gap: 40px !important;

                .date-input-wrapper,
                .payout-input-wrapper {
                    width: 100%;

                    input {
                        width: 100%;
                        max-width: none !important;
                    }
                }
            }
        }
    }

    .error-text {
        color: #f03232 !important;
    }
    .disabled-btn {
        opacity: 0.6;
    }
    p {
        margin-bottom: 0px;
    }
    .title {
        color: #dedede;
        font-family: 'Instrument Sans';
        font-size: 32px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        letter-spacing: -0.64px;
    }
    .edit-opportunity-modal-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 40px;
        max-width: 895px;

        .date-payout-image-container {
            display: flex;
            width: 100%;
            justify-content: space-between;
        }

        .date-payout-fields {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 48px;

            .date-input-wrapper,
            .payout-input-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
                gap: 6px;
                .payout-value-input {
                    color: #dedede;
                    font-family: 'Instrument Sans';
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 120%;
                    letter-spacing: -0.2px;
                    padding: 12px 20px 15px 20px;
                    border-radius: 12px;
                    border: 1.5px solid $darker-grey;
                    background: #262626;
                    max-width: 172px;
                    max-height: 65px;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                }

                .payout-value {
                    position: relative;
                    color: #dedede;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    font-family: 'Instrument Sans';
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 120%;
                    letter-spacing: -0.2px;
                    border: none;
                    background: none;
                    padding-top: 20px;
                    padding-left: 5px;
                }

                .payout-value:focus {
                    outline: none;
                    border: none;
                    box-shadow: none;
                }

                label {
                    margin-top: 16px;
                    color: $darker-grey;
                    font-family: 'Instrument Sans';
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 135%;
                    letter-spacing: 0.12px;
                }
                input {
                    padding: 12px 16px 16px 16px;
                    border-radius: 12px;
                    border: 1px solid $darker-grey;
                    background: #262626;
                    color: #dedede;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;
                    max-width: 172px;
                }
            }
        }

        .event-type-input-wrapper {
            display: flex;
            flex-direction: row;
            gap: 16px;
            align-items: center;
            padding-bottom: 28px;
            margin-top: 48px;

            .event-type-switch-label {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 16px;

                .switch-label {
                    color: $darker-grey;
                    @include base-font-22;
                    line-height: 120%;
                    letter-spacing: -0.22px;
                }
            }

            .invite-artist-description {
                color: $lighter-grey;
                @include base-font-16;
                font-weight: 550;
                line-height: 125%;
            }
        }

        .event-image {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 24px;
            flex: 0 1 35%;

            p {
                color: #dedede;
                font-family: 'Instrument Sans';
                font-size: 26px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.52px;
                margin: 0;
            }

            .image-container {
                min-width: 100%;
                min-height: 212px;
                flex: 1 0 0;
                border-radius: 12px;
                border: 1px solid $darker-grey;
                background: #262626;
                position: relative;
                cursor: pointer;

                .upload-image {
                    object-fit: cover;
                    width: 100%;
                    height: 100%;
                }
                .default-image {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border-radius: 12px;
                }
            }
        }

        .opportunity-detail-field {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 8px;
            width: 100%;

            .opportunity-detail-counter {
                color: #646464;
                font-family: 'Instrument Sans';
                font-size: 12px;
                font-style: normal;
                font-weight: 650;
                line-height: 135%;
                letter-spacing: 0.12px;
                text-align: right;
                align-self: flex-end;
            }
            .opportunity-detail-input {
                max-width: 895px;
                width: 100%;
                height: 127px;
                padding: 12px 15px 15px 15px;
                border-radius: 12px;
                border: 1.5px solid $darker-grey;
                background: #262626;
                color: #dedede;
                font-family: 'Instrument Sans';
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
            }
        }

        .vibe-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            width: 100%;

            .input-err {
                border: 1px solid #e23453 !important;
            }
            .vibe-input {
                border-radius: 12px;
                border: 1px solid $darker-grey;
                background: #262626;
                padding: 12px 16px 16px 16px;
                align-self: stretch;
                color: #dedede;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: 'Instrument Sans';
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
            }
            .keywords {
                margin-top: 24px;
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                max-width: 1044px;
                width: 100%;
                min-height: 36px;
                height: 100%;
                gap: 8px;

                .tag-wrapper {
                    min-height: 40px;

                    span {
                        cursor: pointer;
                        padding: 7px 28px 9px 30px;
                        border-radius: 100px;
                        border: 1px solid #cdeb09;
                        background: #262626 url('/images/icons/delete-yellow.svg') no-repeat
                            92% 50%;
                        background-size: 8px 8px;
                        color: #cdeb09;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }
        }

        .btn-container {
            width: 100%;

            button {
                padding: 11px 30px 12px 30px;
                border-radius: 100px;
                background: #cdeb09;
                color: #000;
                font-family: 'Instrument Sans';
                font-size: 18px;
                font-style: normal;
                font-weight: 550;
                line-height: 120%;
                width: 100%;
                border: 0;
            }
        }
    }
</style>
